import { useEffect, useState } from "react";

// Cache oEmbed lookups per video id so re-renders / repeat links don't refetch.
const cache = new Map();

/**
 * WhatsApp-style rich preview for a YouTube link: thumbnail + play button, the
 * video title and channel. Title/channel come from YouTube's public oEmbed
 * endpoint (no API key, CORS-enabled); the thumbnail is derived straight from
 * the video id so it shows instantly even before oEmbed resolves.
 */
export default function YouTubeCard({ url, videoId, mine }) {
  const [meta, setMeta] = useState(() => cache.get(videoId) || null);

  useEffect(() => {
    if (cache.has(videoId)) {
      setMeta(cache.get(videoId));
      return;
    }
    let active = true;
    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const m = d ? { title: d.title, author: d.author_name } : { title: null, author: null };
        cache.set(videoId, m);
        if (active) setMeta(m);
      })
      .catch(() => {
        const m = { title: null, author: null };
        cache.set(videoId, m);
        if (active) setMeta(m);
      });
    return () => {
      active = false;
    };
  }, [videoId, url]);

  const thumb = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`mb-1.5 block max-w-[300px] overflow-hidden rounded-xl border transition ${
        mine ? "border-white/25 bg-white/10 hover:bg-white/15" : "border-slate-200 bg-surface hover:bg-slate-50"
      }`}
    >
      <div className="relative">
        <img
          src={thumb}
          alt=""
          loading="lazy"
          className="aspect-video w-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55 shadow-lg backdrop-blur-sm">
            <svg className="h-5 w-5 translate-x-[1px] text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 5.5l12 6.5-12 6.5z" />
            </svg>
          </span>
        </span>
      </div>
      <div className="px-3 py-2">
        <span className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide ${mine ? "text-white/75" : "text-rose-500"}`}>
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23 12s0-3.4-.44-5.02a2.6 2.6 0 00-1.83-1.84C19.1 4.7 12 4.7 12 4.7s-7.1 0-8.73.44a2.6 2.6 0 00-1.84 1.84C1 8.6 1 12 1 12s0 3.4.43 5.02c.24.9.94 1.6 1.84 1.84C4.9 19.3 12 19.3 12 19.3s7.1 0 8.73-.44a2.6 2.6 0 001.83-1.84C23 15.4 23 12 23 12zM9.8 15.3V8.7l6 3.3-6 3.3z" />
          </svg>
          YouTube
        </span>
        {/* No `block` next to `line-clamp-2` — `.block` is emitted later in the
            stylesheet and wins the display tie, which stops the clamp working. */}
        <span className={`mt-0.5 line-clamp-2 break-words text-[12.5px] font-bold leading-snug ${mine ? "text-white" : "text-slate-800"}`}>
          {meta?.title || "Watch on YouTube"}
        </span>
        {meta?.author && (
          <span className={`mt-0.5 block truncate text-[11px] ${mine ? "text-white/70" : "text-slate-400"}`}>{meta.author}</span>
        )}
      </div>
    </a>
  );
}
